import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel, FormGroup,
    InputLabel,
    MenuItem,
    Select,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import { getLanguagesForCreateVideo } from '../subtitles/SelectLanguageComponent';
import videoUploadService from '../../services/VideoUploadService';

const Text2TextDialog = ({ 
  open, 
  onClose, 
  videoId, 
  onSuccess, 
  folderPath 
}) => {
  const [loading, setLoading] = useState(false);
  const [sourceLanguageOptions, setSourceLanguageOptions] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [skipTranslation, setSkipTranslation] = useState(false);
  const [allLanguages, setAllLanguages] = useState([]);
  const [videoName, setVideoName] = useState('');

  useEffect(() => {
    if (open && videoId) {
      setLoading(true);
      
      // Get all available languages
      setAllLanguages(getLanguagesForCreateVideo());
      
      // Fetch video details to get available languages
      videoCRUDServiceInstance.getVideoById(videoId)
        .then(response => {
          const videoData = response.data;
          
          // Extract processed languages with VTT files
          let sourceLangs = [];
          if (videoData.videoLanguages && Array.isArray(videoData.videoLanguages)) {
            sourceLangs = videoData.videoLanguages
              .filter(lang => lang.status === 'READY' && lang.vttFileUrl)
              .map(lang => String(lang.languageName));
          }
          
          setSourceLanguageOptions(sourceLangs);
          setVideoName(videoData.videoName);
          // Default to English if available, otherwise first language
          if (sourceLangs.includes('English')) {
            setSelectedSource('English');
          } else if (sourceLangs.length > 0) {
            setSelectedSource(sourceLangs[0]);
          }
          
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching video details:', error);
          setLoading(false);
          onClose();
        });
    } else {
      // Reset state when dialog closes
      setSelectedTargets([]);
      setSkipTranslation(false);
    }
  }, [open, videoId]);

  // This effect removes the selected source language from the target languages list
  useEffect(() => {
    if (selectedSource && selectedTargets.includes(selectedSource)) {
      setSelectedTargets(prev => prev.filter(lang => lang !== selectedSource));
    }
  }, [selectedSource, selectedTargets]);

  const handleTargetChange = (event, langLabel) => {
    if (event.target.checked) {
      setSelectedTargets([...selectedTargets, langLabel]);
    } else {
      setSelectedTargets(selectedTargets.filter(lang => lang !== langLabel));
    }
  };

  const handleSubmit = () => {
    if (selectedTargets.length === 0) {
      return; // Don't proceed if no target languages selected
    }

    setLoading(true);
    
    // Get the current user's credentials
    const userData = JSON.parse(localStorage.getItem('authUser'));
    if (!userData) {
      setLoading(false);
      onClose('error', 'User authentication information not found');
      return;
    }
    
    // Call API to start text2text translation
    videoUploadService.text2Text(
      videoId,
      videoName,
      selectedSource,
      selectedTargets.join(','),
      skipTranslation,
      userData.email,
      userData.access
    )
      .then(response => {
        setLoading(false);
        onSuccess({
          sourceLanguage: selectedSource, 
          targetLanguages: selectedTargets,
          skipTranslation: skipTranslation
        });
        onClose('success', `Translation from ${selectedSource} to ${selectedTargets.length} language(s) has been initiated.`);
      })
      .catch(error => {
        setLoading(false);
        console.error('Error starting translation:', error);
        onClose('error', `Failed to start translation: ${error.response?.data?.message || error.message}`);
      });
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => !loading && onClose()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Text-to-Text Translation</DialogTitle>
      
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}
        
        {!loading && (
          <>
            {sourceLanguageOptions.length === 0 ? (
              <Typography color="error">
                This video does not have any processed languages that can be used as a source for translation.
              </Typography>
            ) : (
              <Box sx={{ p: 1 }}>
                {/* Source Language Dropdown */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="source-language-label">Source Language</InputLabel>
                  <Select
                    labelId="source-language-label"
                    id="source-language"
                    value={selectedSource}
                    label="Source Language"
                    onChange={(e) => setSelectedSource(e.target.value)}
                  >
                    {sourceLanguageOptions.map((lang) => (
                      <MenuItem key={lang} value={lang}>
                        {lang}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Target Languages Checkboxes */}
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                  Target Languages
                </Typography>
                
                <Box sx={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  p: 1,
                  mb: 3
                }}>
                  <FormGroup>
                    {allLanguages.map((langObj) => {
                      const isSourceLanguage = langObj.label === selectedSource;
                      const isAlreadyTranslated = sourceLanguageOptions.includes(langObj.label) && !isSourceLanguage;
                      const isDisabled = isSourceLanguage;
                      
                      // If this is the selected source language, remove it from target selections
                      if (isSourceLanguage && selectedTargets.includes(langObj.label)) {
                        setSelectedTargets(prev => prev.filter(lang => lang !== langObj.label));
                      }
                      
                      return (
                        <FormControlLabel
                          key={langObj.value}
                          control={
                            <Checkbox
                              checked={selectedTargets.includes(langObj.label)}
                              onChange={(e) => handleTargetChange(e, langObj.label)}
                              disabled={isDisabled}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {langObj.label}
                              {isSourceLanguage && (
                                <Typography 
                                  component="span" 
                                  variant="caption" 
                                  color="primary"
                                  sx={{ ml: 1, fontWeight: 500 }}
                                >
                                  (current source)
                                </Typography>
                              )}
                              {isAlreadyTranslated && (
                                <Typography 
                                  component="span" 
                                  variant="caption" 
                                  color="text.secondary"
                                  sx={{ ml: 1 }}
                                >
                                  (already available)
                                </Typography>
                              )}
                            </Typography>
                          }
                          sx={{ my: 0.5 }}
                        />
                      );
                    })}
                  </FormGroup>
                </Box>
                
                {/* Skip Translation Checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={skipTranslation}
                      onChange={(e) => setSkipTranslation(e.target.checked)}
                    />
                  }
                  label="Skip translation (create empty subtitle files)"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={() => onClose()} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading || sourceLanguageOptions.length === 0 || selectedTargets.length === 0}
        >
          Start Translation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Text2TextDialog;